// ============================================================
// A HORDE ON THE DISPLAY IS A BODY PER MAGNITUDE POINT (plan design-gaps, V3;
// the ruling: "a body per magnitude point to 40, then one per two"). The
// bodies are packed inside the horde's base ring - a sunflower spiral, so the
// same count always lies the same way and thinning it only tightens it - and an
// arc round the ring says what share of its whole the horde still has.
//
// Unit-disc positions, for the marker to place on its ring. Pure.
// ============================================================

/** Up to this magnitude a body per point; past it, one per two. */
const ONE_EACH_TO = 40;
/** How far out the outermost body sits, as a share of the ring: room for its own radius. */
const FILL = 0.85;
/** The golden angle, radians: each body turned this far from the last spreads them evenly. */
const GOLDEN = Math.PI * (3 - Math.sqrt(5));

/** How many bodies a horde of this magnitude draws: one per point to forty, then one per two. */
export function hordeBodyCount(magnitude: number): number {
  const m = Math.max(0, Math.floor(magnitude));
  return m <= ONE_EACH_TO ? m : ONE_EACH_TO + Math.ceil((m - ONE_EACH_TO) / 2);
}

/** Each count's layout, worked out once: it depends on nothing else (the Track V review: not every frame). */
const LAYOUTS = new Map<number, readonly { x: number; z: number }[]>();

/** Where each of `count` bodies stands inside the unit ring (x across, z deep): the same for the same count. */
export function hordeLayout(count: number): readonly { x: number; z: number }[] {
  const known = LAYOUTS.get(count);
  if (known) return known;
  const made = Array.from({ length: count }, (_, i) => {
    const r = FILL * Math.sqrt((i + 0.5) / count);
    const a = i * GOLDEN;
    return { x: r * Math.cos(a), z: r * Math.sin(a) };
  });
  LAYOUTS.set(count, made);
  return made;
}

/** The share of its whole the horde still has (0..1): its magnitude arc. A horde with no recorded whole is whole. */
export function magnitudeShare(magnitude: number, max: number): number {
  if (!(max > 0)) return 1;
  return Math.min(1, Math.max(0, magnitude / max));
}
